import React from 'react';
import {
    Card, Box, Typography, TextField, MenuItem, Alert, Switch, FormControlLabel, Chip, Divider,
} from '@mui/material';
import { GALLERY_CONFLUENCE_URL, ASP_DEFAULT_BACKEND } from 'src/constants';
import { TestEntityCard } from './TestEntityCard';
import { TestRunMetrics } from './TestRunMetrics';
import { HelpLink } from '../common/HelpLink';
import { EmptyState } from '../common/EmptyState';
import { isTestRunExperimental } from './utils';

enum RelevanceScore {
    NotScored = 'NotScored',
    NotRelevant = 'NotRelevant',
    Relevant = 'Relevant',
    HighlyRelevant = 'HighlyRelevant',
}

interface PropTypes {
    testRun: Models.IngestConfigApi.RelevanceTests.RelevanceTestRun;
    testRuns?: { results: Models.IngestConfigApi.RelevanceTests.RelevanceTestRun[] };
    scoredEntities: Models.IngestConfigApi.RelevanceTests.ScoredEntity[];
    kValue: number;
    productKey: string;
    includeExperimental: boolean;
    onIncludeExperimentalChange: (include: boolean) => void;
    handleTestRunChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleCommentChange: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        comment: string
    ) => void;
    handleScoredEntityChange: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        score: Models.IngestConfigApi.RelevanceTests.RelevanceScore
    ) => void;
}

const metricsDetailsConfluenceUrl = `${GALLERY_CONFLUENCE_URL}DCI/pages/4683114280/Search+Relevance+Scoring+and+Metrics+Tooling#Evaluation-Metrics`;

export const TestRunResults: React.FC<PropTypes> = ({
    testRun,
    testRuns,
    scoredEntities,
    kValue,
    productKey,
    includeExperimental,
    onIncludeExperimentalChange,
    handleTestRunChange,
    handleCommentChange,
    handleScoredEntityChange,
}) => {
    const unscoredEntities = testRun?.entities?.filter((e) => e.relevance === RelevanceScore.NotScored
        && !scoredEntities.some((s) => s.designConceptId === e.designConceptId)) || [];

    const filteredTestRuns = testRuns?.results.filter(
        (run) => includeExperimental || !isTestRunExperimental(run),
    ) || [];

    const hasExperimentFlags = testRun?.experimentFlags && Object.keys(testRun.experimentFlags).length > 0;

    const getTestRunResultEntityColor = (entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity): string => {
        if (entity.relevance === RelevanceScore.HighlyRelevant) {
            return '#e8f5e9'; // Light green
        }

        if (entity.relevance === RelevanceScore.Relevant) {
            return '#fff8e1'; // Light yellow
        }

        if (entity.relevance === RelevanceScore.NotRelevant) {
            return '#ffebee'; // Light red
        }

        if (scoredEntities?.find((e) => e.designConceptId === entity.designConceptId)) {
            return '#f5f5f5'; // Light grey
        }

        return '#e3f2fd'; // Light blue for unscored
    };

    return (
        <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
                        <Typography variant="h6">Test Run Results</Typography>
                        <HelpLink href={metricsDetailsConfluenceUrl} />
                    </Box>
                    <TextField
                        select
                        label="Test Run"
                        size="small"
                        sx={{ minWidth: '300px', maxWidth: '400px' }}
                        value={testRun?.testRunId || ''}
                        onChange={handleTestRunChange}
                    >
                        <MenuItem
                            sx={{
                                '&:hover': { backgroundColor: 'transparent' },
                                '&.Mui-selected': { backgroundColor: 'transparent' },
                                cursor: 'default',
                            }}
                        >
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={includeExperimental}
                                        size="small"
                                        onChange={(e): void => {
                                            e.stopPropagation();
                                            onIncludeExperimentalChange(e.target.checked);
                                        }}
                                    />
                                )}
                                label="Show experimental"
                                sx={{ fontSize: '0.875rem', width: '100%' }}
                                onClick={(e): void => e.stopPropagation()}
                            />
                        </MenuItem>
                        <Divider />
                        {filteredTestRuns.map((run) => {
                            const flagCount = Object.entries(run.experimentFlags || {}).length;

                            return (
                                <MenuItem key={run.testRunId} value={run.testRunId}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        {new Date(run.runDate).toLocaleString(undefined, {
                                            year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
                                        })}
                                        {run.searchBackend && run.searchBackend !== ASP_DEFAULT_BACKEND && (
                                        <Chip
                                            color={run.searchBackend === ASP_DEFAULT_BACKEND ? 'default' : 'primary'}
                                            label={run.searchBackend}
                                            size="small"
                                            variant="outlined"
                                        />
                                        )}
                                        {run.experimentFlags && flagCount > 0 && (
                                            <Chip
                                                color="success"
                                                label={`${flagCount} ${flagCount > 1 ? 'flags' : 'flag'}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </Box>
                <Box sx={{
                    display: 'flex', flexDirection: 'column', gap: 1, mt: 1,
                }}
                >
                    <Typography color="text.secondary" variant="body2">
                        Run Date:
                        {' '}
                        {new Date(testRun?.runDate).toLocaleString(undefined, {
                            year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
                        })}
                        {' '}
                        | K:
                        {' '}
                        {kValue}
                        {' '}
                        | Scored:
                        {' '}
                        {scoredEntities.length}
                    </Typography>
                    {(testRun?.searchBackend || hasExperimentFlags) && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {testRun?.searchBackend && testRun.searchBackend !== ASP_DEFAULT_BACKEND && (
                                <>
                                    <Typography color="text.secondary" variant="body2">Search Backend:</Typography>
                                    <Chip
                                        color={testRun.searchBackend === ASP_DEFAULT_BACKEND ? 'default' : 'primary'}
                                        label={testRun.searchBackend}
                                        size="small"
                                        variant="outlined"
                                    />
                                </>
                            )}
                            {hasExperimentFlags && (
                                <>
                                    <Typography color="text.secondary" variant="body2">Experiment Flags:</Typography>
                                    {Object.entries(testRun?.experimentFlags || {}).map(([key, value]) => (
                                        <Chip
                                            color="success"
                                            label={`${key}: ${value.toString()}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}

                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            {testRun && (
                <>
                    <TestRunMetrics kValue={kValue} metrics={testRun.metrics || {}} />
                    <Box sx={{ p: 2 }}>
                        <Typography mb={2} variant="body2">
                            This view shows the top
                            {' '}
                            {kValue}
                            {' '}
                            results returned by ASP
                            during this test run.
                        </Typography>

                        <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {unscoredEntities.length > 0 && (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {unscoredEntities.length === 1 ? '1 concept in this run is not currently scored.' : `${unscoredEntities.length} concepts in this run are not currently scored.`}
                                    </Alert>
                                        {unscoredEntities.map((entity) => (
                                            <TestEntityCard
                                                showScoreControls
                                                backgroundColor="#e3f2fd" // Light blue
                                                entity={entity}
                                                handleCommentChange={handleCommentChange}
                                                handleScoredEntityChange={handleScoredEntityChange}
                                                key={entity.designConceptId}
                                                productKey={productKey}
                                                showCommentBox={false}
                                            />
                                        ))}
                                </>
                            )}
                            {testRun?.entities?.filter((e) => e.relevance !== RelevanceScore.NotScored
                        || scoredEntities.some((s) => s.designConceptId === e.designConceptId))
                                .map((entity) => (
                                    <TestEntityCard
                                        backgroundColor={getTestRunResultEntityColor(entity)}
                                        entity={entity}
                                        key={entity.designConceptId}
                                        productKey={productKey}
                                        showCommentBox={false}
                                        showScoreControls={false}
                                    />
                                ))}
                            {testRun?.entities?.length === 0 && (
                                <EmptyState sx={{ p: 4 }}>
                                    <Typography gutterBottom variant="h6">
                                        No Results Returned
                                    </Typography>
                                    <Typography variant="body1">
                                        The test run returned zero results from ASP in its state.
                                    </Typography>
                                </EmptyState>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </Card>
    );
};
